import React from "react";
import { motion } from "framer-motion";
import Button, { ButtonShape, ButtonSize } from "@scripts/react/components/Button";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { ButtonType } from "@scripts/react/components/Button";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { canvasHoverProps } from "@rapid/constants/motion";

type BaseProps = {
    onPrevious: () => void;
    onNext: () => void;
    showKeyboardHotkey?: boolean;
    buttonType?: ButtonType;
    buttonShape?: ButtonShape;
    buttonSize?: ButtonSize;
    disabled?: boolean;
    buttonClassName?: string;
};

function NavigationButtons({
    onPrevious,
    onNext,
    showKeyboardHotkey = true,
    buttonType = "dark-secondary",
    buttonShape = "circle",
    buttonSize = "large",
    disabled = false,
    buttonClassName,
    prevIcon,
    nextIcon,
    prevHotkey,
    nextHotkey
}: BaseProps & { prevIcon: string; nextIcon: string; prevHotkey: string; nextHotkey: string }) {
    return (
        <>
            <TooltipContainer
                type="titleOnly"
                title={
                    <div>
                        Previous{" "}
                        {showKeyboardHotkey && (
                            <span className="hotkey dark ml-1">{prevHotkey}</span>
                        )}
                    </div>
                }
                placement="left"
                customButton={
                    <span>
                        <Button
                            className={buttonClassName}
                            type={buttonType}
                            shape={buttonShape}
                            size={buttonSize}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onPrevious();
                            }}
                            disabled={disabled}
                        >
                            <i className={twClassNames("fa", prevIcon)} />
                        </Button>
                    </span>
                }
            />
            <TooltipContainer
                type="titleOnly"
                title={
                    <div>
                        Next{" "}
                        {showKeyboardHotkey && (
                            <span className="hotkey dark ml-1">{nextHotkey}</span>
                        )}
                    </div>
                }
                placement="left"
                customButton={
                    <span>
                        <Button
                            className={buttonClassName}
                            type={buttonType}
                            shape={buttonShape}
                            size={buttonSize}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onNext();
                            }}
                            disabled={disabled}
                        >
                            <i className={twClassNames("fa", nextIcon)} />
                        </Button>
                    </span>
                }
            />
        </>
    );
}

export function FloatingNavigationControls({
    onPrevious,
    onNext,
    showKeyboardHotkey = true,
    buttonType = "dark-secondary",
    buttonShape = "circle",
    buttonSize = "large",
    disabled = false,
    buttonClassName = "border-opacity-50 shadow-lg hover:scale-105",
    visible = true
}: BaseProps & { visible?: boolean }) {
    return (
        <motion.div
            className="absolute inset-0"
            {...canvasHoverProps(visible, { pointerEvents: "none" })}
        >
            <div
                className={twClassNames(
                    visible ? "pointer-events-auto" : "pointer-events-none",
                    "absolute inset-0 z-50 flex items-center justify-between px-8"
                )}
            >
                <NavigationButtons
                    onPrevious={onPrevious}
                    onNext={onNext}
                    showKeyboardHotkey={showKeyboardHotkey}
                    buttonType={buttonType}
                    buttonShape={buttonShape}
                    buttonSize={buttonSize}
                    disabled={disabled}
                    buttonClassName={buttonClassName}
                    prevIcon="fa-chevron-left"
                    nextIcon="fa-chevron-right"
                    prevHotkey="←"
                    nextHotkey="→"
                />
            </div>
        </motion.div>
    );
}

export function InlineNavigationControls({
    onPrevious,
    onNext,
    showKeyboardHotkey = true,
    buttonType = "dark-ghost",
    buttonShape = "square",
    buttonSize = "xxsmall",
    disabled = false,
    buttonClassName,
    visible = true
}: BaseProps & { visible?: boolean }) {
    return (
        <div className={twClassNames("flex", !visible && "pointer-events-none opacity-0")}>
            <NavigationButtons
                onPrevious={onPrevious}
                onNext={onNext}
                showKeyboardHotkey={showKeyboardHotkey}
                buttonType={buttonType}
                buttonShape={buttonShape}
                buttonSize={buttonSize}
                disabled={disabled}
                buttonClassName={buttonClassName}
                prevIcon="fa-chevron-up"
                nextIcon="fa-chevron-down"
                prevHotkey="↑"
                nextHotkey="↓"
            />
        </div>
    );
}
